// Pure reads behind the Territories list and detail (WEB_PLAN.md §5.8; ports of TerritoryListView, TerritoryCard,
// TerritoryDetailView and Territories+Store.swift). The rules themselves (membership, stats, area, overlap) are
// domain/territories.ts; this module adds the screen logic around them (scopes, search, card text) and runs the
// domain functions over pre-narrowed inputs so a company with hundreds of territories and thousands of stores is not
// O(territories × stores × vertices) on every data change.

import { TERRITORY_KIND_TITLES, type DistanceUnit } from '@/contracts/enums';
import type { ActivityPayload, PipelinePayload, RecordPayload, TerritoryPayload, UserPayload } from '@/contracts/models';
import type { Role } from '@/contracts/wire';
import { addDays } from '@/domain/calendar';
import { effectiveMs } from '@/domain/derived';
import type { Coordinate } from '@/domain/geo';
import { formatArea, recordsInTerritory, territoryArea, territoryStats, type TerritoryStats } from '@/domain/territories';
import { compareLocalized, fullName, hasId, idKey, lookup, sameId, trimSpaces } from '@/domain/util';

/** iOS falls back to this when a territory has no name. */
export const UNTITLED_TERRITORY = 'Untitled territory';

export function territoryName(t: Pick<TerritoryPayload, 'name'>): string {
  return t.name.trim() === '' ? UNTITLED_TERRITORY : t.name;
}

// MARK: - Visibility, scopes and search (TerritoryListView)

/**
 * The territories the list shows: every live item, including ones a newer app wrote (`undecodable`, best-effort
 * payload). Those stay read-only and get a badge (data/README.md); dropping them would leave no way to reach them
 * from the list. The envelope id wins, so the card links to the item the detail page can load.
 */
export function listedTerritories(
  items: readonly { id: string; value: TerritoryPayload; undecodable?: string }[],
): { territories: TerritoryPayload[]; newer: Set<string> } {
  const territories: TerritoryPayload[] = [];
  const newer = new Set<string>();
  for (const item of items) {
    territories.push(sameId(item.value.id, item.id) ? item.value : { ...item.value, id: item.id });
    if (item.undecodable !== undefined) newer.add(idKey(item.id));
  }
  return { territories, newer };
}

/**
 * `AppStore.visibleTerritories`: managers and admins see every territory; reps see the ones they are assigned to,
 * plus unassigned ones when the company lets reps create territories. Sorted by name (localized, case-insensitive).
 */
export function visibleTerritories(
  territories: readonly TerritoryPayload[],
  viewer: { profileId: string | null | undefined; role: Role | null | undefined },
  allowRepsToCreateTerritories: boolean,
): TerritoryPayload[] {
  const sorted = [...territories].sort((a, b) => compareLocalized(a.name, b.name));
  if (viewer.role !== 'rep' || !viewer.profileId) return sorted;
  const me = viewer.profileId;
  return sorted.filter(
    (t) => t.assignedUserIds.some((id) => sameId(id, me)) || (allowRepsToCreateTerritories && t.assignedUserIds.length === 0),
  );
}

export const TERRITORY_SCOPES = ['all', 'mine', 'unassigned'] as const;
export type TerritoryScope = (typeof TERRITORY_SCOPES)[number];

export const TERRITORY_SCOPE_TITLES: Readonly<Record<TerritoryScope, string>> = {
  all: 'All',
  mine: 'Mine',
  unassigned: 'Unassigned',
};

/** The scope tabs a role gets (`scopeTitles`): managers also get "Unassigned". */
export function scopesFor(isManager: boolean): TerritoryScope[] {
  return isManager ? ['all', 'mine', 'unassigned'] : ['all', 'mine'];
}

/** The scope segment: Mine = the viewer is assigned; Unassigned = nobody is. */
export function scopeTerritories(
  list: readonly TerritoryPayload[],
  scope: TerritoryScope,
  viewerId: string | null | undefined,
): TerritoryPayload[] {
  switch (scope) {
    case 'all':
      return [...list];
    case 'mine':
      return viewerId ? list.filter((t) => t.assignedUserIds.some((id) => sameId(id, viewerId))) : [];
    case 'unassigned':
      return list.filter((t) => t.assignedUserIds.length === 0);
  }
}

/**
 * The search box ("Search territories, ZIPs, reps"): trimmed, lower-cased substring of the name, any ZIP code, or the
 * full name of an assigned rep (`userName`, so an unknown id matches "unassigned" as on iOS).
 */
export function searchTerritories(
  list: readonly TerritoryPayload[],
  query: string,
  users: ReadonlyMap<string, Pick<UserPayload, 'firstName' | 'lastName'>>,
): TerritoryPayload[] {
  const q = trimSpaces(query).toLowerCase();
  if (q === '') return [...list];
  return list.filter(
    (t) =>
      t.name.toLowerCase().includes(q) ||
      t.postalCodes.some((code) => code.toLowerCase().includes(q)) ||
      t.assignedUserIds.some((id) => {
        const u = lookup(users, id);
        return (u ? fullName(u) : 'Unassigned').toLowerCase().includes(q);
      }),
  );
}

export interface TerritorySections {
  /** Everything the scope and search keep (drives the empty state and the summary line). */
  all: TerritoryPayload[];
  active: TerritoryPayload[];
  /** Shown under "Inactive", dimmed. */
  inactive: TerritoryPayload[];
}

/** Scope, then search, then split into the active list and the "Inactive" section. */
export function territoryListSections(
  visible: readonly TerritoryPayload[],
  options: {
    scope: TerritoryScope;
    query: string;
    viewerId: string | null | undefined;
    users: ReadonlyMap<string, Pick<UserPayload, 'firstName' | 'lastName'>>;
  },
): TerritorySections {
  const all = searchTerritories(scopeTerritories(visible, options.scope, options.viewerId), options.query, options.users);
  return { all, active: all.filter((t) => t.isActive), inactive: all.filter((t) => !t.isActive) };
}

/** "3 territories · 1,240 stores · 4 reps" (the list's summary strip; reps counted once across territories). */
export function territorySummary(list: readonly TerritoryPayload[], storeCount: (t: TerritoryPayload) => number): {
  territories: number;
  stores: number;
  reps: number;
} {
  let stores = 0;
  const reps = new Set<string>();
  for (const t of list) {
    stores += storeCount(t);
    for (const id of t.assignedUserIds) reps.add(idKey(id));
  }
  return { territories: list.length, stores, reps: reps.size };
}

export function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

// MARK: - Card and header text

/** `TerritoryCard.kindText`: "Custom map area · 2.40 sq mi", "County · no boundary yet", "3 codes · 46320, 46321, 46322". */
export function territoryKindText(t: Pick<TerritoryPayload, 'kind' | 'polygon' | 'postalCodes'>, unit: DistanceUnit): string {
  const title = TERRITORY_KIND_TITLES[t.kind];
  if (t.kind === 'postalCodes') {
    const n = t.postalCodes.length;
    if (n === 0) return `${title} · none yet`;
    return `${n} ${plural(n, 'code', 'codes')} · ${t.postalCodes.slice(0, 3).join(', ')}${n > 3 ? '…' : ''}`;
  }
  if (t.polygon.length < 3) return `${title} · no boundary yet`;
  return `${title} · ${formatArea(territoryArea(t), unit)}`;
}

/** `TerritoryDetailView.areaText`: the hero chip ("2.40 sq mi" or "3 codes"); undefined when there is nothing yet. */
export function territoryAreaText(t: Pick<TerritoryPayload, 'kind' | 'polygon' | 'postalCodes'>, unit: DistanceUnit): string | undefined {
  if (t.kind === 'postalCodes') {
    const n = t.postalCodes.length;
    return n === 0 ? undefined : `${n} ${plural(n, 'code', 'codes')}`;
  }
  return t.polygon.length >= 3 ? formatArea(territoryArea(t), unit) : undefined;
}

/** Whether the kind is drawn as a shape (`TerritoryKind.usesPolygon`): custom areas and counties. */
export function usesPolygon(t: Pick<TerritoryPayload, 'kind'>): boolean {
  return t.kind !== 'postalCodes';
}

/** The hero's "40% of stores won" line (`winRateText`); undefined without stores or wins. */
export function winRateText(stats: Pick<TerritoryStats, 'records' | 'won'>): string | undefined {
  if (stats.records <= 0 || stats.won <= 0) return undefined;
  return `${Math.round((stats.won / stats.records) * 100)}% of stores won`;
}

// MARK: - Membership and stats, fast

type Box = { west: number; south: number; east: number; north: number };

/**
 * The polygon's bounding box over every vertex, or undefined when it has fewer than 3 points or a non-finite one
 * (then no store can be narrowed out). Even-odd ray casting only ever marks a point inside when it lies within the
 * vertices' bounding box, so narrowing to the box first cannot change `recordsInTerritory`'s answer.
 */
function polygonBox(polygon: readonly Coordinate[]): Box | undefined {
  if (polygon.length < 3) return undefined;
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  for (const p of polygon) {
    if (!Number.isFinite(p.latitude) || !Number.isFinite(p.longitude)) return undefined;
    west = Math.min(west, p.longitude);
    east = Math.max(east, p.longitude);
    south = Math.min(south, p.latitude);
    north = Math.max(north, p.latitude);
  }
  return { west, south, east, north };
}

function inBox(c: Coordinate | undefined, b: Box): boolean {
  return !!c && c.longitude >= b.west && c.longitude <= b.east && c.latitude >= b.south && c.latitude <= b.north;
}

/**
 * `AppStore.records(in:)` for one territory (non-archived stores tagged with it or inside its shape), computed on the
 * stores whose tag matches or whose coordinate falls in the shape's bounding box. Same result as
 * `recordsInTerritory(t, records)`, without testing every store against every polygon.
 */
export function storesInTerritory<R extends RecordPayload>(t: TerritoryPayload, records: readonly R[]): R[] {
  const box = polygonBox(t.polygon);
  if (t.polygon.length >= 3 && !box) return recordsInTerritory(t, records);
  const candidates = records.filter((r) => sameId(r.territoryId, t.id) || (box !== undefined && inBox(r.coordinate, box)));
  return recordsInTerritory(t, candidates);
}

/** Stores per territory (lower-case territory id → stores), one narrowed pass per territory. */
export function storesByTerritory<R extends RecordPayload>(
  territories: readonly TerritoryPayload[],
  records: readonly R[],
): Map<string, R[]> {
  const out = new Map<string, R[]>();
  for (const t of territories) out.set(idKey(t.id), storesInTerritory(t, records));
  return out;
}

/**
 * Completed visits whose effective date is in the last 30 days: the only activities `territoryStats` counts, so
 * passing these instead of every activity gives the same numbers with one pass over the activities for all territories.
 */
export function recentVisits(activities: readonly ActivityPayload[], now: Date): ActivityPayload[] {
  const since = addDays(now, -30).getTime();
  return activities.filter((a) => a.type === 'visit' && a.status === 'completed' && effectiveMs(a) >= since);
}

/**
 * `territoryStats` for every territory: stores, visits in 30 days, won, open pipeline value (lower-case id → stats).
 * `members` is `storesByTerritory` over the same records (pass it to avoid a second pass).
 */
export function territoryStatsMap(
  territories: readonly TerritoryPayload[],
  data: {
    records: readonly RecordPayload[];
    activities: readonly ActivityPayload[];
    pipelines: readonly PipelinePayload[];
    members?: ReadonlyMap<string, readonly RecordPayload[]>;
  },
  now: Date = new Date(),
): Map<string, TerritoryStats> {
  const visits = recentVisits(data.activities, now);
  const out = new Map<string, TerritoryStats>();
  for (const t of territories) {
    const stores = data.members?.get(idKey(t.id)) ?? storesInTerritory(t, data.records);
    out.set(idKey(t.id), territoryStats(t, { records: stores, activities: visits, pipelines: data.pipelines }, now));
  }
  return out;
}

export const EMPTY_TERRITORY_STATS: Readonly<TerritoryStats> = Object.freeze({ records: 0, visits30d: 0, won: 0, pipelineValue: 0 });

// MARK: - Detail: assigned reps

export interface AssignedRep {
  user: UserPayload;
  /** Stores in the territory this person owns. */
  storeCount: number;
}

/**
 * The detail's "Assigned reps" rows: each assigned id that is a known person, in the territory's order, with how many
 * of the territory's stores they own. Unknown ids are skipped, as on iOS.
 */
export function assignedReps(
  t: Pick<TerritoryPayload, 'assignedUserIds'>,
  users: ReadonlyMap<string, UserPayload>,
  stores: readonly Pick<RecordPayload, 'ownerId'>[],
): AssignedRep[] {
  const counts = new Map<string, number>();
  for (const r of stores) {
    if (!r.ownerId) continue;
    const k = idKey(r.ownerId);
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  const seen = new Set<string>();
  const out: AssignedRep[] = [];
  for (const id of t.assignedUserIds) {
    const k = idKey(id);
    if (seen.has(k)) continue;
    seen.add(k);
    const user = lookup(users, id);
    if (user) out.push({ user, storeCount: counts.get(k) ?? 0 });
  }
  return out;
}

/** The viewer's visible stores among a territory's stores (iOS filters the detail list by `visibleRecords`). */
export function visibleOnly<R extends RecordPayload>(stores: readonly R[], visibleIds: ReadonlySet<string>): R[] {
  return stores.filter((r) => hasId(visibleIds, r.id));
}

